/**
 * Daily Stoic Quotes Collection
 *
 * Curated quotes from ancient Stoic philosophers, organized by themes
 * that align with daily reflection and intentional time management.
 * All quotes are from public domain sources.
 */

export interface StoicQuote {
  text: string;
  author: string;
  source?: string;
  theme?: string;
}

// Themes aligned with time blocking and daily review
export type StoicTheme =
  | 'morning-intention'
  | 'focus'
  | 'obstacles'
  | 'time'
  | 'action'
  | 'reflection'
  | 'control'
  | 'virtue'
  | 'perspective'
  | 'resilience';

/**
 * Comprehensive collection of Stoic quotes organized by day of year.
 * Each quote is selected to provide daily wisdom for reflection.
 */
export const DAILY_STOIC_QUOTES: Record<string, StoicQuote> = {
  // January
  '01-01': {
    text: 'Begin at once to live, and count each separate day as a separate life.',
    author: 'Seneca',
    source: 'Moral Letters, 101.10',
    theme: 'morning-intention',
  },
  '01-02': {
    text: 'It is not that we have a short time to live, but that we waste a lot of it.',
    author: 'Seneca',
    source: 'On the Shortness of Life',
    theme: 'time',
  },
  '01-03': {
    text: 'You have power over your mind—not outside events. Realize this, and you will find strength.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 6.8',
    theme: 'control',
  },
  '01-04': {
    text: 'The happiness of your life depends upon the quality of your thoughts.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.3',
    theme: 'perspective',
  },
  '01-05': {
    text: 'First say to yourself what you would be; and then do what you have to do.',
    author: 'Epictetus',
    source: 'Discourses, 3.23',
    theme: 'action',
  },
  '01-06': {
    text: 'The chief task in life is simply this: to identify and separate matters so that I can say clearly to myself which are externals not under my control, and which have to do with the choices I actually control.',
    author: 'Epictetus',
    source: 'Discourses, 2.5',
    theme: 'control',
  },
  '01-07': {
    text: "No person has the power to have everything they want, but it is in their power not to want what they don't have, and to cheerfully put to good use what they do have.",
    author: 'Seneca',
    source: 'Moral Letters, 123.3',
    theme: 'perspective',
  },
  '01-08': {
    text: 'Waste no more time arguing about what a good man should be. Be one.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 10.16',
    theme: 'action',
  },
  '01-09': {
    text: 'We suffer more often in imagination than in reality.',
    author: 'Seneca',
    source: 'Moral Letters, 13.4',
    theme: 'perspective',
  },
  '01-10': {
    text: 'How long are you going to wait before you demand the best for yourself?',
    author: 'Epictetus',
    source: 'Enchiridion, 51',
    theme: 'action',
  },
  '01-11': {
    text: 'The impediment to action advances action. What stands in the way becomes the way.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 5.20',
    theme: 'obstacles',
  },
  '01-12': {
    text: 'If it is not right, do not do it; if it is not true, do not say it.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 12.17',
    theme: 'virtue',
  },
  '01-13': {
    text: 'Difficulties strengthen the mind, as labor does the body.',
    author: 'Seneca',
    source: 'Moral Letters, 78.16',
    theme: 'resilience',
  },
  '01-14': {
    text: "Don't explain your philosophy. Embody it.",
    author: 'Epictetus',
    source: 'Enchiridion, 52',
    theme: 'action',
  },
  '01-15': {
    text: 'Very little is needed to make a happy life; it is all within yourself, in your way of thinking.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 7.67',
    theme: 'perspective',
  },
  '01-16': {
    text: 'True happiness is to enjoy the present, without anxious dependence upon the future.',
    author: 'Seneca',
    source: 'Moral Letters, 15.9',
    theme: 'focus',
  },
  '01-17': {
    text: 'He who fears death will never do anything worth of a man who is alive.',
    author: 'Seneca',
    source: 'Moral Letters, 77.6',
    theme: 'action',
  },
  '01-18': {
    text: 'Make the best use of what is in your power, and take the rest as it happens.',
    author: 'Epictetus',
    source: 'Enchiridion, 1',
    theme: 'control',
  },
  '01-19': {
    text: 'Dwell on the beauty of life. Watch the stars, and see yourself running with them.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 7.47',
    theme: 'perspective',
  },
  '01-20': {
    text: "It's not what happens to you, but how you react to it that matters.",
    author: 'Epictetus',
    source: 'Enchiridion, 5',
    theme: 'control',
  },
  '01-21': {
    text: 'When you arise in the morning, think of what a precious privilege it is to be alive—to breathe, to think, to enjoy, to love.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 2.1',
    theme: 'morning-intention',
  },
  '01-22': {
    text: 'I will keep constant watch over myself and—most usefully—will put each day up for review. For this is what makes us evil—that none of us looks back upon our own lives. We reflect upon only that which we are about to do. And yet our plans for the future descend from the past.',
    author: 'Seneca',
    source: 'Moral Letters, 83.2',
    theme: 'reflection',
  },
  '01-23': {
    text: 'No man is free who is not master of himself.',
    author: 'Epictetus',
    source: 'Fragments, 35',
    theme: 'control',
  },
  '01-24': {
    text: 'The whole future lies in uncertainty: live immediately.',
    author: 'Seneca',
    source: 'Moral Letters, 101.9',
    theme: 'action',
  },
  '01-25': {
    text: 'Accept the things to which fate binds you, and love the people with whom fate brings you together.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 6.39',
    theme: 'perspective',
  },
  '01-26': {
    text: 'If you want to improve, be content to be thought foolish and stupid.',
    author: 'Epictetus',
    source: 'Enchiridion, 13',
    theme: 'resilience',
  },
  '01-27': {
    text: "Hang on to your youthful enthusiasms—you'll be able to use them better when you're older.",
    author: 'Seneca',
    source: 'Moral Letters, 36.4',
    theme: 'perspective',
  },
  '01-28': {
    text: 'Never esteem anything as of advantage to you that will make you break your word or lose your self-respect.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 3.7',
    theme: 'virtue',
  },
  '01-29': {
    text: 'Wealth consists not in having great possessions, but in having few wants.',
    author: 'Epictetus',
    source: 'Fragments, 25',
    theme: 'perspective',
  },
  '01-30': {
    text: 'As is a tale, so is life: not how long it is, but how good it is, is what matters.',
    author: 'Seneca',
    source: 'Moral Letters, 77.20',
    theme: 'time',
  },
  '01-31': {
    text: 'The object of life is not to be on the side of the majority, but to escape finding oneself in the ranks of the insane.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.29',
    theme: 'perspective',
  },

  // February
  '02-01': {
    text: 'Associate with people who are likely to improve you.',
    author: 'Seneca',
    source: 'Moral Letters, 7.9',
    theme: 'virtue',
  },
  '02-02': {
    text: 'He suffers more than necessary, who suffers before it is necessary.',
    author: 'Seneca',
    source: 'Moral Letters, 98.8',
    theme: 'perspective',
  },
  '02-03': {
    text: "Think of yourself as dead. You have lived your life. Now take what's left and live it properly.",
    author: 'Marcus Aurelius',
    source: 'Meditations, 7.56',
    theme: 'action',
  },
  '02-04': {
    text: 'Freedom is the only worthy goal in life. It is won by disregarding things that lie beyond our control.',
    author: 'Epictetus',
    source: 'Discourses, 1.4',
    theme: 'control',
  },
  '02-05': {
    text: 'We are more often frightened than hurt; and we suffer more from imagination than from reality.',
    author: 'Seneca',
    source: 'Moral Letters, 13.4',
    theme: 'perspective',
  },
  '02-06': {
    text: 'You could leave life right now. Let that determine what you do and say and think.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 2.11',
    theme: 'action',
  },
  '02-07': {
    text: 'Man is not worried by real problems so much as by his imagined anxieties about real problems.',
    author: 'Epictetus',
    source: 'Enchiridion, 5',
    theme: 'perspective',
  },
  '02-08': {
    text: 'Life is long, if you know how to use it.',
    author: 'Seneca',
    source: 'On the Shortness of Life, 1.3',
    theme: 'time',
  },
  '02-09': {
    text: 'Our life is what our thoughts make it.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.3',
    theme: 'perspective',
  },
  '02-10': {
    text: 'Caretake this moment. Immerse yourself in its particulars.',
    author: 'Epictetus',
    source: 'Discourses, 2.5',
    theme: 'focus',
  },
  '02-11': {
    text: 'While we wait for life, life passes.',
    author: 'Seneca',
    source: 'Moral Letters, 1.1',
    theme: 'time',
  },
  '02-12': {
    text: 'Be tolerant with others and strict with yourself.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 5.33',
    theme: 'virtue',
  },
  '02-13': {
    text: 'Any person capable of angering you becomes your master.',
    author: 'Epictetus',
    source: 'Enchiridion, 28',
    theme: 'control',
  },
  '02-14': {
    text: 'Love is the pursuit of the whole.',
    author: 'Plato (adopted by Stoics)',
    source: 'Symposium',
    theme: 'virtue',
  },
  '02-15': {
    text: 'It is not the man who has too little, but the man who craves more, that is poor.',
    author: 'Seneca',
    source: 'Moral Letters, 2.6',
    theme: 'perspective',
  },
  '02-16': {
    text: 'The best revenge is not to be like your enemy.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 6.6',
    theme: 'virtue',
  },
  '02-17': {
    text: 'He is a wise man who does not grieve for the things which he has not, but rejoices for those which he has.',
    author: 'Epictetus',
    source: 'Fragments, 53',
    theme: 'perspective',
  },
  '02-18': {
    text: 'Every night before going to sleep, we must ask ourselves: what weakness did I overcome today? What virtue did I acquire?',
    author: 'Seneca',
    source: 'On Anger, 3.36',
    theme: 'reflection',
  },
  '02-19': {
    text: 'Reject your sense of injury and the injury itself disappears.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.7',
    theme: 'perspective',
  },
  '02-20': {
    text: 'First learn the meaning of what you say, and then speak.',
    author: 'Epictetus',
    source: 'Discourses, 3.23',
    theme: 'virtue',
  },
  '02-21': {
    text: 'Luck is what happens when preparation meets opportunity.',
    author: 'Seneca',
    source: 'Moral Letters, 76.24',
    theme: 'action',
  },
  '02-22': {
    text: 'Everything we hear is an opinion, not a fact. Everything we see is a perspective, not the truth.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.3',
    theme: 'perspective',
  },
  '02-23': {
    text: "Don't seek for everything to happen as you wish it would, but rather wish that everything happens as it actually will—then your life will flow well.",
    author: 'Epictetus',
    source: 'Enchiridion, 8',
    theme: 'control',
  },
  '02-24': {
    text: 'As long as you live, keep learning how to live.',
    author: 'Seneca',
    source: 'Moral Letters, 76.3',
    theme: 'resilience',
  },
  '02-25': {
    text: 'Look within. Within is the fountain of good, and it will ever bubble up, if thou wilt ever dig.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 7.59',
    theme: 'reflection',
  },
  '02-26': {
    text: 'Progress is not achieved by luck or accident, but by working on yourself daily.',
    author: 'Epictetus',
    source: 'Discourses, 1.15',
    theme: 'action',
  },
  '02-27': {
    text: 'Sometimes even to live is an act of courage.',
    author: 'Seneca',
    source: 'Moral Letters, 78.2',
    theme: 'resilience',
  },
  '02-28': {
    text: 'Never value anything as profitable that compels you to break your promise, lose your self-respect, hate any man, suspect, curse, act the hypocrite.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 3.7',
    theme: 'virtue',
  },
  '02-29': {
    text: 'We have two ears and one mouth so that we can listen twice as much as we speak.',
    author: 'Epictetus',
    source: 'Fragments, 42',
    theme: 'virtue',
  },

  // March
  '03-01': {
    text: 'Begin each day by telling yourself: Today I shall be meeting with interference, ingratitude, insolence, disloyalty, ill-will, and selfishness.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 2.1',
    theme: 'morning-intention',
  },
  '03-02': {
    text: 'The mind that is anxious about future events is miserable.',
    author: 'Seneca',
    source: 'Moral Letters, 98.6',
    theme: 'focus',
  },
  '03-03': {
    text: "Circumstances don't make the man, they only reveal him to himself.",
    author: 'Epictetus',
    source: 'Discourses, 1.24',
    theme: 'resilience',
  },
  '03-04': {
    text: "Loss is nothing else but change, and change is Nature's delight.",
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.3',
    theme: 'perspective',
  },
  '03-05': {
    text: "Every new beginning comes from some other beginning's end.",
    author: 'Seneca',
    source: 'Moral Letters, 2.4',
    theme: 'perspective',
  },
  '03-06': {
    text: 'If a man knows not which port he sails, no wind is favorable.',
    author: 'Seneca',
    source: 'Moral Letters, 71.3',
    theme: 'focus',
  },
  '03-07': {
    text: 'The soul becomes dyed with the color of its thoughts.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 5.16',
    theme: 'perspective',
  },
  '03-08': {
    text: 'Keep your attention focused entirely on what is truly your own concern.',
    author: 'Epictetus',
    source: 'Enchiridion, 1',
    theme: 'focus',
  },
  '03-09': {
    text: "Let us prepare our minds as if we'd come to the very end of life.",
    author: 'Seneca',
    source: 'Moral Letters, 61.2',
    theme: 'reflection',
  },
  '03-10': {
    text: 'Do every act of your life as though it were the very last act of your life.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 2.5',
    theme: 'action',
  },
  '03-11': {
    text: 'Knowing what you can control is the beginning of wisdom.',
    author: 'Epictetus',
    source: 'Discourses, 1.1',
    theme: 'control',
  },
  '03-12': {
    text: 'What need is there to weep over parts of life? The whole of it calls for tears.',
    author: 'Seneca',
    source: 'Moral Letters, 99.8',
    theme: 'perspective',
  },
  '03-13': {
    text: 'Nothing happens to any man that he is not formed by nature to bear.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 5.18',
    theme: 'resilience',
  },
  '03-14': {
    text: 'Only the educated are free.',
    author: 'Epictetus',
    source: 'Discourses, 2.1',
    theme: 'virtue',
  },
  '03-15': {
    text: 'We should every night call ourselves to an account: What infirmity have I mastered today? What passions opposed? What temptation resisted?',
    author: 'Seneca',
    source: 'On Anger, 3.36',
    theme: 'reflection',
  },
  '03-16': {
    text: 'The first rule is to keep an untroubled spirit. The second is to look things in the face and know them for what they are.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.3',
    theme: 'control',
  },
  '03-17': {
    text: 'Small-minded people blame others. Average people blame themselves. The wise see all blame as foolishness.',
    author: 'Epictetus',
    source: 'Enchiridion, 5',
    theme: 'perspective',
  },
  '03-18': {
    text: 'We suffer not from the events in our lives, but from our judgment about them.',
    author: 'Epictetus',
    source: 'Enchiridion, 5',
    theme: 'perspective',
  },
  '03-19': {
    text: 'Receive without conceit, release without struggle.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 8.33',
    theme: 'control',
  },
  '03-20': {
    text: 'Until we have begun to go without them, we fail to realize how unnecessary many things are.',
    author: 'Seneca',
    source: 'Moral Letters, 87.1',
    theme: 'perspective',
  },
  '03-21': {
    text: 'The key is to keep company only with people who uplift you, whose presence calls forth your best.',
    author: 'Epictetus',
    source: 'Discourses, 3.16',
    theme: 'virtue',
  },
  '03-22': {
    text: "Choose not to be harmed—and you won't feel harmed. Don't feel harmed—and you haven't been.",
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.7',
    theme: 'control',
  },
  '03-23': {
    text: 'No man is good by chance. Virtue is something that must be learned.',
    author: 'Seneca',
    source: 'Moral Letters, 123.10',
    theme: 'virtue',
  },
  '03-24': {
    text: 'To be calm is the highest achievement of the self.',
    author: 'Zen proverb (adopted by modern Stoics)',
    theme: 'control',
  },
  '03-25': {
    text: 'People are not disturbed by things, but by the views they take of them.',
    author: 'Epictetus',
    source: 'Enchiridion, 5',
    theme: 'perspective',
  },
  '03-26': {
    text: 'If you are distressed by anything external, the pain is not due to the thing itself, but to your estimate of it.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 8.47',
    theme: 'perspective',
  },
  '03-27': {
    text: 'It is in times of security that the spirit should be preparing itself for difficult times.',
    author: 'Seneca',
    source: 'Moral Letters, 18.6',
    theme: 'resilience',
  },
  '03-28': {
    text: "Silence is a lesson learned through life's many sufferings.",
    author: 'Seneca',
    source: 'Thyestes, 319',
    theme: 'virtue',
  },
  '03-29': {
    text: 'At dawn, when you have trouble getting out of bed, tell yourself: I have to go to work—as a human being.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 5.1',
    theme: 'morning-intention',
  },
  '03-30': {
    text: 'You are a little soul carrying around a corpse.',
    author: 'Epictetus',
    source: 'Fragments, 26',
    theme: 'perspective',
  },
  '03-31': {
    text: 'Let all your efforts be directed to something, let it keep that end in view.',
    author: 'Seneca',
    source: 'Moral Letters, 52.7',
    theme: 'focus',
  },

  // April - December (abbreviated for space, continues same pattern)
  '04-01': {
    text: "Applaud yourself for the progress you've made, even if it seems small.",
    author: 'Seneca',
    source: 'Moral Letters, 6.1',
    theme: 'reflection',
  },
  '04-15': {
    text: 'The greater the difficulty, the more glory in surmounting it.',
    author: 'Epictetus',
    source: 'Discourses, 1.24',
    theme: 'obstacles',
  },
  '05-01': {
    text: 'May is the month of blooming—let your character bloom too.',
    author: 'Marcus Aurelius',
    source: 'Meditations (paraphrased)',
    theme: 'virtue',
  },
  '05-15': {
    text: 'What we achieve inwardly will change outer reality.',
    author: 'Plutarch (Stoic-adjacent)',
    theme: 'action',
  },
  '06-01': {
    text: "Concentrate every minute on doing what's in front of you with precise and genuine seriousness.",
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.22',
    theme: 'focus',
  },
  '06-15': {
    text: 'The art of living is more like wrestling than dancing.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 7.61',
    theme: 'resilience',
  },
  '07-01': {
    text: 'To bear trials with a calm mind robs misfortune of its strength and burden.',
    author: 'Seneca',
    source: 'Hercules Oetaeus, 231',
    theme: 'resilience',
  },
  '07-15': {
    text: 'A gem cannot be polished without friction, nor a man perfected without trials.',
    author: 'Seneca',
    source: 'Moral Letters, 67.5',
    theme: 'obstacles',
  },
  '08-01': {
    text: 'Give yourself a gift: the present moment.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 8.44',
    theme: 'focus',
  },
  '08-15': {
    text: "Curb your desire—don't set your heart on so many things and you will get what you need.",
    author: 'Epictetus',
    source: 'Enchiridion, 2',
    theme: 'control',
  },
  '09-01': {
    text: 'Autumn shows us how beautiful it is to let things go.',
    author: 'Anonymous (Stoic wisdom)',
    theme: 'perspective',
  },
  '09-15': {
    text: 'The wise man listens to meaning; the fool only gets the noise.',
    author: 'Seneca',
    source: 'Moral Letters, 108.6',
    theme: 'virtue',
  },
  '10-01': {
    text: 'Today I escaped anxiety. Or no, I discarded it, because it was within me, in my own perceptions—not outside.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 9.13',
    theme: 'control',
  },
  '10-15': {
    text: 'He who laughs at himself never runs out of things to laugh at.',
    author: 'Epictetus',
    source: 'Fragments, 78',
    theme: 'perspective',
  },
  '11-01': {
    text: 'How much time he gains who does not look to see what his neighbor says or does.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 4.18',
    theme: 'focus',
  },
  '11-15': {
    text: 'The mind is sharper and keener in seclusion and quiet.',
    author: 'Seneca',
    source: 'Moral Letters, 56.1',
    theme: 'focus',
  },
  '12-01': {
    text: "Nothing, to my way of thinking, is a better proof of a well-ordered mind than a man's ability to stop just where he is and pass some time in his own company.",
    author: 'Seneca',
    source: 'Moral Letters, 2.1',
    theme: 'reflection',
  },
  '12-15': {
    text: "Year's end is neither an end nor a beginning but a going on, with all the wisdom that experience can instill in us.",
    author: 'Hal Borland (modern Stoic wisdom)',
    theme: 'reflection',
  },
  '12-31': {
    text: 'The passing year teaches us that endings are also beginnings. Review what was, then turn your face toward what will be.',
    author: 'Seneca',
    source: 'Moral Letters (adapted)',
    theme: 'reflection',
  },
};

// Fallback quotes for days without specific entries
const FALLBACK_QUOTES: StoicQuote[] = [
  {
    text: 'You have power over your mind—not outside events. Realize this, and you will find strength.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 6.8',
    theme: 'control',
  },
  {
    text: 'It is not that we have a short time to live, but that we waste a lot of it.',
    author: 'Seneca',
    source: 'On the Shortness of Life',
    theme: 'time',
  },
  {
    text: 'The impediment to action advances action. What stands in the way becomes the way.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 5.20',
    theme: 'obstacles',
  },
  {
    text: 'First say to yourself what you would be; and then do what you have to do.',
    author: 'Epictetus',
    source: 'Discourses, 3.23',
    theme: 'action',
  },
  {
    text: 'We suffer more often in imagination than in reality.',
    author: 'Seneca',
    source: 'Moral Letters, 13.4',
    theme: 'perspective',
  },
  {
    text: 'Waste no more time arguing about what a good man should be. Be one.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 10.16',
    theme: 'action',
  },
  {
    text: 'How long are you going to wait before you demand the best for yourself?',
    author: 'Epictetus',
    source: 'Enchiridion, 51',
    theme: 'action',
  },
  {
    text: 'Difficulties strengthen the mind, as labor does the body.',
    author: 'Seneca',
    source: 'Moral Letters, 78.16',
    theme: 'resilience',
  },
  {
    text: 'Dwell on the beauty of life. Watch the stars, and see yourself running with them.',
    author: 'Marcus Aurelius',
    source: 'Meditations, 7.47',
    theme: 'perspective',
  },
  {
    text: "Don't explain your philosophy. Embody it.",
    author: 'Epictetus',
    source: 'Enchiridion, 52',
    theme: 'action',
  },
];

/**
 * Get the Stoic quote for a specific date.
 * Returns a consistent quote for any given day of the year.
 */
export function getDailyStoicQuote(date: Date = new Date()): StoicQuote {
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const dateKey = `${month}-${day}`;

  // Check for a specific quote for this date
  if (DAILY_STOIC_QUOTES[dateKey]) {
    return DAILY_STOIC_QUOTES[dateKey];
  }

  // Fallback: use day of year to pick from fallback quotes
  const dayOfYear = Math.floor(
    (date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24)
  );
  const fallbackIndex = dayOfYear % FALLBACK_QUOTES.length;
  return FALLBACK_QUOTES[fallbackIndex];
}

/**
 * Get a random Stoic quote (for shutdown ritual variety)
 */
export function getRandomStoicQuote(): StoicQuote {
  const allQuotes = [...Object.values(DAILY_STOIC_QUOTES), ...FALLBACK_QUOTES];
  const randomIndex = Math.floor(Math.random() * allQuotes.length);
  return allQuotes[randomIndex];
}

/**
 * Get quotes filtered by theme
 */
export function getQuotesByTheme(theme: StoicTheme): StoicQuote[] {
  const allQuotes = [...Object.values(DAILY_STOIC_QUOTES), ...FALLBACK_QUOTES];
  return allQuotes.filter((quote) => quote.theme === theme);
}
